import React, { useState, useEffect } from 'react'
import { Box, Paper, Typography, CircularProgress, Alert, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { CheckCircle, Warning, Error, Info, ExpandMore } from '@mui/icons-material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { getEngineByIdentifier } from '../../../../../apiRoutes/powerlinkRoutes'
import { getEngineVariables } from '../../../../../apiRoutes'

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#8cc63f' },
        secondary: { main: '#4caf50' },
        background: { default: '#1a1a1a', paper: '#2d2d2d' },
        text: { primary: '#ffffff', secondary: '#b0b0b0' },
    },
})

interface PowerLinkViewProps {
    row: {
        engineIdentifier?: string | any
        deviceSerial?: string | any
        device?: string | any
    }
}

const PowerLinkView: React.FC<PowerLinkViewProps> = ({ row }) => {
    const [engineData, setEngineData] = useState<any>(null)
    const [engineVariables, setEngineVariables] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Helper function to get calibration status
    const getCalibrationStatus = () => {
        if (!engineData?.engineCalibrationFiles || !engineVariables?.CalChecksumCurrent) {
            return { status: 'unknown', message: 'Insufficient data to check calibration status' }
        }

        const activeCalibration = engineData.engineCalibrationFiles.find(
            (file: any) => file.engineCalibrationActive === true
        )

        if (!activeCalibration) {
            return { status: 'warning', message: 'No active calibration file found' }
        }

        // Helper function to normalize checksums (remove 0x prefix and convert to uppercase)
        const normalizeChecksum = (checksum: string) => {
            if (!checksum) return ''
            return checksum.replace(/^0x/i, '').toUpperCase()
        }

        const currentChecksum = engineVariables.CalChecksumCurrent.value
        const expectedChecksum = activeCalibration.engineCalibrationChecksum
        const normalizedCurrentChecksum = normalizeChecksum(currentChecksum)
        const normalizedExpectedChecksum = normalizeChecksum(expectedChecksum)

        // Find the current engine file to get its revision
        const currentEngineFile = engineData.engineCalibrationFiles.find(
            (file: any) => normalizeChecksum(file.engineCalibrationChecksum) === normalizedCurrentChecksum
        )

        if (normalizedCurrentChecksum === normalizedExpectedChecksum) {
            return {
                status: 'success',
                message: 'Up to date',
                currentChecksum,
                expectedChecksum,
                currentRevision: currentEngineFile?.engineCalibrationRevision || 'Unknown',
                expectedRevision: activeCalibration.engineCalibrationRevision || 'Unknown'
            }
        } else {
            return {
                status: 'error',
                message: 'Outdated',
                currentChecksum,
                expectedChecksum,
                currentRevision: currentEngineFile?.engineCalibrationRevision || 'Unknown',
                expectedRevision: activeCalibration.engineCalibrationRevision || 'Unknown'
            }
        }
    }

    // Helper function to get engine details
    const getEngineDetails = () => {
        if (!engineVariables) return null

        return {
            // ClickHouse data
            engineMake: engineVariables.engineMake?.value || 'N/A',
            engineModel: engineVariables.engineModel?.value || 'N/A',
            engineSerial: engineVariables.engineSerial?.value || 'N/A',
            engineUnitNum: engineVariables.engineUnitNum?.value || 'N/A',
            ecuHardwarePartNum: engineVariables.ecuHardwarePartNum?.value || 'N/A',
            ecuSerial: engineVariables.ecuSerial?.value || 'N/A',
            ecuLocation: engineVariables.ecuLocation?.value || 'N/A',
            ecuType: engineVariables.ecuType?.value || 'N/A',
            custSoftwareName: engineVariables.CustSoftwareName?.value || 'N/A',
            initialCalModel: engineVariables.InitialCalModel?.value || 'N/A',
            // PowerLink API data
            powerLinkEngineIdentifier: engineData?.engineIdentifier || 'N/A',
            powerLinkEngineName: engineData?.engineName || 'N/A',
            powerLinkEngineDescription: engineData?.engineDescription || 'N/A',
            powerLinkCalibrationFilesCount: engineData?.engineCalibrationFiles?.length || 0
        }
    }

    // Helper function to get calibration files info
    const getCalibrationFilesInfo = () => {
        if (!engineData?.engineCalibrationFiles) return null

        const files = engineData.engineCalibrationFiles
        const activeFile = files.find((file: any) => file.engineCalibrationActive === true)
        const totalFiles = files.length
        const currentEngineChecksum = engineVariables?.CalChecksumCurrent?.value

        // Helper function to normalize checksums (remove 0x prefix and convert to uppercase)
        const normalizeChecksum = (checksum: string) => {
            if (!checksum) return ''
            return checksum.replace(/^0x/i, '').toUpperCase()
        }

        const normalizedCurrentChecksum = normalizeChecksum(currentEngineChecksum)

        return {
            totalFiles,
            activeFile: activeFile ? {
                checksum: activeFile.engineCalibrationChecksum,
                name: activeFile.engineCalibrationFileName || 'Unknown',
                version: activeFile.engineCalibrationRevision || 'Unknown'
            } : null,
            allFiles: files.map((file: any) => ({
                ...file, // Include all file properties
                isCurrentEngine: normalizedCurrentChecksum && normalizeChecksum(file.engineCalibrationChecksum) === normalizedCurrentChecksum
            }))
        }
    }

    useEffect(() => {
        const fetchEngineData = async () => {
            // First, get engine variables from ClickHouse to find engineIdentifier
            let deviceSerial: string | null = null

            // Try to get deviceSerial from various possible locations
            if (typeof row.deviceSerial === 'string') {
                deviceSerial = row.deviceSerial
            } else if (typeof row.device === 'string') {
                deviceSerial = row.device
            } else if (row.deviceSerial && typeof row.deviceSerial === 'object') {
                const deviceObj = row.deviceSerial as any
                deviceSerial = deviceObj.deviceSerial || deviceObj.device || deviceObj._id || deviceObj.id
            }

            if (!deviceSerial) {
                setError('No device serial found for fetching engine variables')
                // console.log('Row object:', row)
                return
            }

            setLoading(true)
            setError(null)

            try {
                // First, fetch engine variables from ClickHouse
                // console.log('Fetching engine variables for device:', deviceSerial)
                const variables = await getEngineVariables(deviceSerial)
                // console.log('ClickHouse Engine Variables Response:', variables)
                setEngineVariables(variables)

                // Look for engineIdentifier in the ClickHouse response
                let engineIdentifier: string | null = null

                // Check if engineIdentifier is in the ClickHouse data
                if (variables && variables.engineIdentifier) {
                    engineIdentifier = variables.engineIdentifier.value
                    // console.log('Found engineIdentifier in ClickHouse data:', engineIdentifier)
                } else {
                    // console.log('No engineIdentifier found in ClickHouse data')
                    // console.log('Available variables:', Object.keys(variables || {}))
                }

                // If we found engineIdentifier, fetch PowerLink data
                if (engineIdentifier) {
                    // console.log('Fetching PowerLink data for engineIdentifier:', engineIdentifier)
                    const powerLinkData = await getEngineByIdentifier(engineIdentifier)
                    // console.log('PowerLink API Response:', powerLinkData)
                    setEngineData(powerLinkData)
                } else {
                    // console.log('Skipping PowerLink API call - no engineIdentifier found')
                    setEngineData(null)
                }

            } catch (err: any) {
                // console.error('Error fetching engine data:', err)
                setError(err.message || 'Failed to fetch engine data')
            } finally {
                setLoading(false)
            }
        }

        fetchEngineData()
    }, [row.deviceSerial, row.device])


    if (loading) {
        return (
            <ThemeProvider theme={theme}>
                <Box sx={{ width: '100%', minHeight: '75vh', bgcolor: 'background.default' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                        <Typography variant="body1" sx={{ ml: 2, color: 'text.primary' }}>
                            Loading PowerLink data...
                        </Typography>
                    </Box>
                </Box>
            </ThemeProvider>
        )
    }

    if (error) {
        return (
            <ThemeProvider theme={theme}>
                <Box sx={{ width: '100%', minHeight: '75vh', bgcolor: 'background.default' }}>
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">
                            {error}
                        </Alert>
                    </Box>
                </Box>
            </ThemeProvider>
        )
    }

    const calibrationStatus = getCalibrationStatus()
    const engineDetails = getEngineDetails()
    const calibrationFilesInfo = getCalibrationFilesInfo()

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: '100%', bgcolor: 'background.default', minHeight: '50vh', }}>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Calibration Status Widget */}
                <Paper sx={{ p: 3, backgroundColor: 'background.default',}}>
                    {/* <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}> */}
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                            Engine Calibration Status
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {calibrationStatus.status === 'success' && <CheckCircle color="success" sx={{ fontSize: 32 }} />}
                        {calibrationStatus.status === 'error' && <Error color="error" sx={{ fontSize: 32 }} />}
                        {calibrationStatus.status === 'warning' && <Warning color="warning" sx={{ fontSize: 32 }} />}
                        {calibrationStatus.status === 'unknown' && <Info color="info" sx={{ fontSize: 32 }} />}

                        <Box>
                            <Typography variant="h6" color={calibrationStatus.status === 'success' ? 'success.main' :
                                                          calibrationStatus.status === 'error' ? 'error.main' :
                                                          calibrationStatus.status === 'warning' ? 'warning.main' : 'info.main'}>
                                {calibrationStatus.message}
                            </Typography>
                            {calibrationStatus.currentChecksum && (
                                <Typography variant="body2" color="text.secondary">
                                    Current: {calibrationStatus.currentChecksum} (Rev: {calibrationStatus.currentRevision}) | Expected: {calibrationStatus.expectedChecksum} (Rev: {calibrationStatus.expectedRevision})
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {calibrationFilesInfo && (
                        <Box sx={{ mt: 2 }}>
                            {/* All Calibration Files Dropdown */}
                            <Accordion sx={{ mt: 1, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMore />}
                                    aria-controls="calibration-files-content"
                                    id="calibration-files-header"
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        View All Calibration Files ({calibrationFilesInfo.totalFiles})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {calibrationFilesInfo.allFiles.map((file: any, index: number) => (
                                            <Accordion key={index} sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore />}
                                                    sx={{
                                                        backgroundColor: 'background.paper',
                                                        borderRadius: '4px 4px 0 0',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        minHeight: 'auto',
                                                        '&.Mui-expanded': {
                                                            minHeight: 'auto',
                                                            borderRadius: '4px 4px 0 0'
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                                                {file.engineCalibrationChecksum}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Rev: {file.engineCalibrationRevision}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            {file.engineCalibrationActive && (
                                                                <Chip
                                                                    label="Active File"
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {file.isCurrentEngine && (
                                                                <Chip
                                                                    label="Current File"
                                                                    size="small"
                                                                    color="warning"
                                                                    variant="filled"
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{
                                                    backgroundColor: 'background.paper',
                                                    borderRadius: '0 0 4px 4px',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderTop: 'none',
                                                    mt: 0
                                                }}>
                                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">File Name:</Typography>
                                                            <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                                                                {file.engineCalibrationFileName || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Production Date:</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {file.engineCalibrationProductionDate ?
                                                                    new Date(file.engineCalibrationProductionDate).toLocaleDateString() : 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Severity Level:</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {file.engineCalibrationSeverityLevel || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Approved:</Typography>
                                                            <Typography variant="body2" fontWeight="bold" color={file.engineCalibrationApproved ? 'success.main' : 'error.main'}>
                                                                {file.engineCalibrationApproved ? 'Yes' : 'No'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Updated By:</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {file.updatedBy || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Created:</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {file.createdAt ?
                                                                    new Date(file.createdAt).toLocaleString() : 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {file.updatedAt ?
                                                                    new Date(file.updatedAt).toLocaleString() : 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    )}
                </Paper>

                {/* Engine Details */}
                {engineDetails && (
                    <Paper sx={{ p: 3, backgroundColor: 'background.default'}}>
                        {/* <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}> */}
                        <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                            Engine Information
                        </Typography>

                        <Box sx={{ mb: 0 }}>
                            {/* Basic Engine Information */}
                            <Box sx={{
                                p: 3,
                                // backgroundColor: 'background.paper',
                                // borderRadius: 1,
                                // border: '1px solid',
                                // borderColor: 'divider',
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
                                gap: 3
                            }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Engine Identifier:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.powerLinkEngineIdentifier}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Engine Name:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.powerLinkEngineName}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Make:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.engineMake}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Model:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.engineModel}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">ECU Hardware:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.ecuHardwarePartNum}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">ECU Serial:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.ecuSerial}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">ECU Location:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.ecuLocation}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">ECU Type:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.ecuType}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Serial:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.engineSerial}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Unit Number:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.engineUnitNum}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Software Name:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.custSoftwareName}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Initial Cal Model:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{engineDetails.initialCalModel}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                )}

                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    )
}

export default PowerLinkView